export const mapBackendGuestTypeToInternal = (guestType?: string): string => {
  switch (guestType?.toLowerCase()) {
    case "contratista":
      return "contractor";
    case "visitante":
      return "visitor";
    case "eventual":
      return "eventual";
    default:
      return guestType || "";
  }
};

/**
 * Un evento de transporte se reconoce por su tag o por traer autorizaciones de transporte: su
 * `guestType` es "any" y no dice nada. Se chequea antes que el resto porque además puede traer
 * `authorizations`, que lo harían pasar por visitante.
 */
export const isTransportEvent = (event: any): boolean => {
  if (!event) return false;
  const target = event.event || event;
  const tags = target.authorizationTags || target.authorizationTypes || [];
  return tags.includes("transport") || (target.transportAuthorizations || []).length > 0;
};

/**
 * El acompañante de un transportista egresa en su propia pestaña: su evento de ingreso apunta al
 * del chofer por `mainEvent` / `driver` (ver processCompanions en useEvent.ts).
 */
export const isCompanionEvent = (event: any): boolean => {
  if (!event) return false;
  const target = event.event || event;
  return !!(target.mainEvent || target.driver);
};

const resolveGuestTypeFromEvent = (entranceEvent: any): string | undefined => {
  if (!entranceEvent) return undefined;

  const event = entranceEvent.event || entranceEvent;

  // 0. Transporte: gana sobre todo lo demás (ver isTransportEvent)
  if (isTransportEvent(event)) {
    return "transport";
  }

  // 1. Check invites first
  const invites = event.invites || [];
  for (const i of invites) {
    const type = (i.type || "").toLowerCase();
    const guestsType = (i.guestsType || "").toLowerCase();

    // "guard" means employeeConfirmation was used — check guestsType for the actual access method
    if (type === "guard") {
      if (guestsType === "contractor" || guestsType === "contratista") {
        return "contractor";
      }
      if (guestsType === "eventual") {
        return "eventual";
      }
      if (guestsType === "invite" || guestsType === "visitor" || guestsType === "visitante") {
        return "visitor";
      }
      // Fallback: if guestsType is missing/unrecognized, treat as mapped guestsType or "employee"
      return guestsType ? mapBackendGuestTypeToInternal(i.guestsType) : "employee";
    }

    if (type === "contractor" || type === "contratista") {
      return "contractor";
    }
    if (type === "eventual") {
      return "eventual";
    }
    if (type === "invite" || type === "visitor" || type === "visitante") {
      return "visitor";
    }
    if (type === "employee" || type === "empleado") {
      return "employee";
    }

    // If type is empty, fall back to guestsType
    if (!type && guestsType) {
      if (guestsType === "contractor" || guestsType === "contratista") {
        return "contractor";
      }
      if (guestsType === "eventual") {
        return "eventual";
      }
      if (guestsType === "invite" || guestsType === "visitor" || guestsType === "visitante") {
        return "visitor";
      }
    }
  }

  // 2. If no invites, check prevalidations
  const prevalidations = event.prevalidations || event.prevalidation || [];
  if (prevalidations.length > 0) {
    return "contractor";
  }

  // 3. Check authorizations
  const authorizations = event.authorizations || [];
  if (authorizations.length > 0) {
    return "visitor";
  }

  // 4. Fallback to guestType mapped to internal
  if (event.guestType) {
    return mapBackendGuestTypeToInternal(event.guestType);
  }

  return undefined;
};

/**
 * Acompañante que entró con un transportista. Se mira la resolución cruda del evento y no
 * `isTransportEvent`, porque el evento del acompañante no lleva `transportAuthorizations` propias
 * (processCompanions las descarta): lo que lo delata es el `guestsType: "transport"` de su
 * confirmación en guardia, que sólo se ve resolviendo el evento entero.
 */
export const isTransportCompanionEvent = (event: any): boolean =>
  isCompanionEvent(event) && resolveGuestTypeFromEvent(event) === "transport";

export const determineGuestTypeFromEvent = (entranceEvent: any): string | undefined => {
  const resolved = resolveGuestTypeFromEvent(entranceEvent);

  // El acompañante no tiene autorización de transporte propia: entra confirmado en guardia por el
  // mismo anfitrión que el chofer. Su egreso arranca entonces como visitante — el guardia puede
  // pasarlo a transporte desde las tarjetas si hiciera falta (ver AccessTypeForm).
  if (resolved === "transport" && isCompanionEvent(entranceEvent)) {
    return "visitor";
  }

  return resolved;
};

/**
 * Maps a pass/invite `type` field to the internal access method used in the form.
 * For `guard` type, looks at `guestsType` to determine the actual access method.
 */
export const mapPassTypeToAccessMethod = (
  passType?: string,
  guestsType?: string
): string | undefined => {
  const type = passType?.toLowerCase();
  const guests = guestsType?.toLowerCase();

  if (type === "guard") {
    // Guard type → look inside guestsType
    if (guests === "contractor" || guests === "contratista") return "contractor";
    if (guests === "eventual") return "eventual";
    if (guests === "invite" || guests === "visitor" || guests === "visitante" || guests === "any")
      return "visitor";
    return guests ? mapBackendGuestTypeToInternal(guestsType) : undefined;
  }

  switch (type) {
    case "invite":
    case "visitor":
    case "visitante":
      return "visitor";
    case "contractor":
    case "contratista":
      return "contractor";
    case "eventual":
      return "eventual";
    default:
      return undefined;
  }
};

/**
 * Maps a pass/invite `type` field to the authorization `_source` value
 * used for checkbox selection in the form.
 * For `guard` type, looks at `guestsType` to determine the source.
 */
export const mapPassTypeToSource = (
  passType?: string,
  guestsType?: string
): string => {
  const type = passType?.toLowerCase();
  const guests = guestsType?.toLowerCase();

  if (type === "guard") {
    if (guests === "contractor" || guests === "contratista" || guests === "eventual") {
      return "prevalidation";
    }
    return "invite"; // visitor/invite/any → invite source
  }

  switch (type) {
    case "invite":
    case "visitor":
    case "visitante":
      return "invite";
    case "contractor":
    case "contratista":
    case "eventual":
      return "prevalidation";
    default:
      return "invite"; // safe fallback
  }
};
